// Filtering and sorting for the Nurses table, kept separate so it's testable.

const DAY_MS = 24 * 60 * 60 * 1000;
export const NO_UNIT = '__none__';

// Epoch ms of the nurse's last sign-in, or null if they've never signed in
// (or the timestamp is unusable). The table shows "Never" for the same rule,
// so filtering and sorting always match what's on screen.
export function lastLoginMs(nurse) {
  if (!nurse.hasLoggedIn || !nurse.lastLoginAt) return null;
  const ms = new Date(nurse.lastLoginAt).getTime();
  return Number.isNaN(ms) ? null : ms;
}

// Rolling windows (the last N × 24 hours), not calendar weeks or months.
// "More than 30 days ago" leaves out nurses who never signed in: lapsed and
// never-started are different follow-ups.
export const LAST_LOGIN_FILTERS = [
  { value: '', label: 'Any time', test: () => true },
  { value: '7d', label: 'Past 7 days', test: (ms, now) => ms !== null && now - ms <= 7 * DAY_MS },
  { value: '30d', label: 'Past 30 days', test: (ms, now) => ms !== null && now - ms <= 30 * DAY_MS },
  { value: 'over30', label: 'More than 30 days ago', test: (ms, now) => ms !== null && now - ms > 30 * DAY_MS },
  { value: 'never', label: 'Never', test: (ms) => ms === null },
];

export function unitOptions(nurses) {
  const units = new Map();
  nurses.forEach((n) => { if (n.unit) units.set(n.unit.id, n.unit.name); });
  return [
    { value: '', label: 'All' },
    ...[...units].sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }))
      .map(([value, label]) => ({ value, label })),
    { value: NO_UNIT, label: 'No unit' },
  ];
}

export function filterNurses(nurses, { query = '', unitId = '', login = '' }, now = Date.now()) {
  const q = query.trim().toLowerCase();
  const loginTest = (LAST_LOGIN_FILTERS.find((f) => f.value === login) || LAST_LOGIN_FILTERS[0]).test;
  return nurses.filter((n) =>
    (!unitId || (unitId === NO_UNIT ? !n.unit : n.unit?.id === unitId)) &&
    loginTest(lastLoginMs(n), now) &&
    (!q ||
      (n.name || '').toLowerCase().includes(q) ||
      (n.email || '').toLowerCase().includes(q) ||
      (n.unit?.name || '').toLowerCase().includes(q)));
}

// direction: null (as returned), 'desc' (newest first) or 'asc'. Nurses who
// never signed in always go last.
export function sortByLastLogin(nurses, direction) {
  if (!direction) return nurses;
  return [...nurses].sort((a, b) => {
    const am = lastLoginMs(a);
    const bm = lastLoginMs(b);
    if (am === null && bm === null) return 0;
    if (am === null) return 1;
    if (bm === null) return -1;
    return direction === 'desc' ? bm - am : am - bm;
  });
}
